import { useState } from "react";
import { DashboardLayout } from "@/components/Dashboard/DashboardLayout";
import { useAuth } from "@/contexts/AuthContext";
import { api } from "@/lib/apiClient";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Plus,
  Search,
  Package,
  Edit,
  Trash2,
  Euro,
  Tag,
  Layers,
} from "lucide-react";
import { toast } from "sonner";
import { formatCurrency } from "@/lib/billingUtils";
import type { TaxRate } from "@/types/billing";

interface BillingArticle {
  id: string;
  code: string;
  name: string;
  description?: string;
  category?: string;
  is_service: boolean;
  unit: string;
  sale_price: number;
  cost_price: number;
  default_tax_rate_id?: string;
  material_inventory_id?: string;
  is_active: boolean;
  notes?: string;
  created_at: string;
}

const emptyArticle: Partial<BillingArticle> = {
  code: '',
  name: '',
  description: '',
  category: '',
  is_service: false,
  unit: 'UND',
  sale_price: 0,
  cost_price: 0,
  is_active: true,
};

export default function BillingArticles() {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [searchTerm, setSearchTerm] = useState("");
  const [categoryFilter, setCategoryFilter] = useState<string>("all");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingArticle, setEditingArticle] = useState<Partial<BillingArticle>>(emptyArticle);
  const [isEditing, setIsEditing] = useState(false);

  // Fetch articles
  const { data: articles = [], isLoading } = useQuery<BillingArticle[]>({
    queryKey: ["billing-articles"],
    queryFn: async () => {
      const { data, error } = await api
        .from("billing_articles")
        .select("*")
        .order("code");
      if (error) throw error;
      return data || [];
    },
  });

  // Fetch tax rates
  const { data: taxRates = [] } = useQuery<TaxRate[]>({
    queryKey: ["tax-rates"],
    queryFn: async () => {
      const { data, error } = await api
        .from("tax_rates")
        .select("*")
        .eq("is_active", true)
        .order("rate", { ascending: false });
      if (error) throw error;
      return data || [];
    },
  });

  // Get unique categories
  const categories = [...new Set(articles.map(a => a.category).filter(Boolean))];

  // Filter articles
  const filteredArticles = articles.filter(article => {
    const matchesSearch = searchTerm === "" ||
      article.code.toLowerCase().includes(searchTerm.toLowerCase()) ||
      article.name.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = categoryFilter === "all" || article.category === categoryFilter;
    return matchesSearch && matchesCategory;
  });

  // Save mutation
  const saveMutation = useMutation({
    mutationFn: async () => {
      if (!editingArticle.code || !editingArticle.name) {
        throw new Error("El código y nombre son obligatorios");
      }

      const data = {
        ...editingArticle,
        updated_at: new Date().toISOString(),
      };

      if (isEditing && editingArticle.id) {
        const { error } = await api
          .from("billing_articles")
          .update(data)
          .eq("id", editingArticle.id);
        if (error) throw error;
      } else {
        const { error } = await api
          .from("billing_articles")
          .insert({ ...data, created_by: user?.id });
        if (error) throw error;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["billing-articles"] });
      setDialogOpen(false);
      setEditingArticle(emptyArticle);
      toast.success(isEditing ? "Artículo actualizado" : "Artículo creado");
    },
    onError: (e: any) => toast.error(e.message),
  });

  // Delete mutation
  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await api
        .from("billing_articles")
        .delete()
        .eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["billing-articles"] });
      toast.success("Artículo eliminado");
    },
    onError: (e: any) => toast.error(e.message),
  });

  const openNewDialog = () => {
    setEditingArticle(emptyArticle);
    setIsEditing(false);
    setDialogOpen(true);
  };

  const openEditDialog = (article: BillingArticle) => {
    setEditingArticle(article);
    setIsEditing(true);
    setDialogOpen(true);
  };

  return (
    <DashboardLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold">Artículos de Facturación</h1>
            <p className="text-muted-foreground">
              Catálogo de productos y servicios para facturar
            </p>
          </div>
          <Button onClick={openNewDialog}>
            <Plus className="w-4 h-4 mr-2" />
            Nuevo artículo
          </Button>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-4">
                <div className="p-3 bg-blue-100 rounded-lg">
                  <Package className="w-6 h-6 text-blue-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{articles.length}</p>
                  <p className="text-sm text-muted-foreground">Total artículos</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-4">
                <div className="p-3 bg-green-100 rounded-lg">
                  <Tag className="w-6 h-6 text-green-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{articles.filter(a => a.is_active).length}</p>
                  <p className="text-sm text-muted-foreground">Activos</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-4">
                <div className="p-3 bg-purple-100 rounded-lg">
                  <Layers className="w-6 h-6 text-purple-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{articles.filter(a => a.is_service).length}</p>
                  <p className="text-sm text-muted-foreground">Servicios</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-4">
                <div className="p-3 bg-amber-100 rounded-lg">
                  <Euro className="w-6 h-6 text-amber-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{categories.length}</p>
                  <p className="text-sm text-muted-foreground">Categorías</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Filters */}
        <Card>
          <CardContent className="pt-6">
            <div className="flex gap-4">
              <div className="flex-1 relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  placeholder="Buscar por código o nombre..."
                  className="pl-10"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>
              <Select value={categoryFilter} onValueChange={setCategoryFilter}>
                <SelectTrigger className="w-48">
                  <SelectValue placeholder="Categoría" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todas las categorías</SelectItem>
                  {categories.map((cat) => (
                    <SelectItem key={cat} value={cat!}>{cat}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </CardContent>
        </Card>

        {/* Table */}
        <Card>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Código</TableHead>
                <TableHead>Nombre</TableHead>
                <TableHead>Categoría</TableHead>
                <TableHead>Tipo</TableHead>
                <TableHead className="text-right">Precio Venta</TableHead>
                <TableHead className="text-right">Precio Coste</TableHead>
                <TableHead>Estado</TableHead>
                <TableHead className="text-right">Acciones</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoading ? (
                <TableRow>
                  <TableCell colSpan={8} className="text-center py-8 text-muted-foreground">
                    Cargando artículos...
                  </TableCell>
                </TableRow>
              ) : filteredArticles.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} className="text-center py-8 text-muted-foreground">
                    No se encontraron artículos
                  </TableCell>
                </TableRow>
              ) : (
                filteredArticles.map((article) => (
                  <TableRow key={article.id}>
                    <TableCell className="font-mono">{article.code}</TableCell>
                    <TableCell>
                      <div>
                        <p className="font-medium">{article.name}</p>
                        {article.description && (
                          <p className="text-sm text-muted-foreground truncate max-w-xs">
                            {article.description}
                          </p>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>{article.category || '-'}</TableCell>
                    <TableCell>
                      <Badge variant={article.is_service ? "secondary" : "outline"}>
                        {article.is_service ? 'Servicio' : 'Producto'}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right font-mono">
                      {formatCurrency(article.sale_price)}
                    </TableCell>
                    <TableCell className="text-right font-mono text-muted-foreground">
                      {formatCurrency(article.cost_price)}
                    </TableCell>
                    <TableCell>
                      <Badge variant={article.is_active ? "default" : "secondary"}>
                        {article.is_active ? 'Activo' : 'Inactivo'}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-2">
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => openEditDialog(article)}
                        >
                          <Edit className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => {
                            if (confirm('¿Eliminar este artículo?')) {
                              deleteMutation.mutate(article.id);
                            }
                          }}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </Card>

        {/* Dialog */}
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>
                {isEditing ? 'Editar artículo' : 'Nuevo artículo'}
              </DialogTitle>
              <DialogDescription>
                {isEditing ? 'Modifica los datos del artículo' : 'Crea un nuevo artículo para facturación'}
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-4 py-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Código *</Label>
                  <Input
                    value={editingArticle.code || ''}
                    onChange={(e) => setEditingArticle({
                      ...editingArticle,
                      code: e.target.value.toUpperCase()
                    })}
                    placeholder="ART-001"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Nombre *</Label>
                  <Input
                    value={editingArticle.name || ''}
                    onChange={(e) => setEditingArticle({
                      ...editingArticle,
                      name: e.target.value
                    })}
                    placeholder="Nombre del artículo"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label>Descripción</Label>
                <Textarea
                  value={editingArticle.description || ''}
                  onChange={(e) => setEditingArticle({
                    ...editingArticle,
                    description: e.target.value
                  })}
                  placeholder="Descripción detallada..."
                  rows={2}
                />
              </div>

              <div className="grid grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label>Categoría</Label>
                  <Input
                    value={editingArticle.category || ''}
                    onChange={(e) => setEditingArticle({
                      ...editingArticle,
                      category: e.target.value
                    })}
                    placeholder="Ej: Servicios"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Unidad</Label>
                  <Select
                    value={editingArticle.unit || 'UND'}
                    onValueChange={(v) => setEditingArticle({
                      ...editingArticle,
                      unit: v
                    })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="UND">Unidad</SelectItem>
                      <SelectItem value="KG">Kilogramo</SelectItem>
                      <SelectItem value="M">Metro</SelectItem>
                      <SelectItem value="M2">Metro²</SelectItem>
                      <SelectItem value="L">Litro</SelectItem>
                      <SelectItem value="H">Hora</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>IVA por defecto</Label>
                  <Select
                    value={editingArticle.default_tax_rate_id || ''}
                    onValueChange={(v) => setEditingArticle({
                      ...editingArticle,
                      default_tax_rate_id: v || undefined
                    })}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="21% (general)" />
                    </SelectTrigger>
                    <SelectContent>
                      {taxRates.map((rate) => (
                        <SelectItem key={rate.id} value={rate.id}>
                          {rate.name} ({rate.rate}%)
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Precio de venta (€)</Label>
                  <Input
                    type="number"
                    step="0.01"
                    min="0"
                    value={editingArticle.sale_price || 0}
                    onChange={(e) => setEditingArticle({
                      ...editingArticle,
                      sale_price: parseFloat(e.target.value) || 0
                    })}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Precio de coste (€)</Label>
                  <Input
                    type="number"
                    step="0.01"
                    min="0"
                    value={editingArticle.cost_price || 0}
                    onChange={(e) => setEditingArticle({
                      ...editingArticle,
                      cost_price: parseFloat(e.target.value) || 0
                    })}
                  />
                </div>
              </div>

              <div className="flex items-center justify-between p-4 bg-muted rounded-lg">
                <div className="space-y-1">
                  <p className="font-medium">Es un servicio</p>
                  <p className="text-sm text-muted-foreground">
                    Los servicios no tienen stock físico
                  </p>
                </div>
                <Switch
                  checked={editingArticle.is_service || false}
                  onCheckedChange={(v) => setEditingArticle({
                    ...editingArticle,
                    is_service: v
                  })}
                />
              </div>

              <div className="flex items-center justify-between p-4 bg-muted rounded-lg">
                <div className="space-y-1">
                  <p className="font-medium">Artículo activo</p>
                  <p className="text-sm text-muted-foreground">
                    Los artículos inactivos no aparecen en facturas
                  </p>
                </div>
                <Switch
                  checked={editingArticle.is_active ?? true}
                  onCheckedChange={(v) => setEditingArticle({
                    ...editingArticle,
                    is_active: v
                  })}
                />
              </div>
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => setDialogOpen(false)}>
                Cancelar
              </Button>
              <Button
                onClick={() => saveMutation.mutate()}
                disabled={saveMutation.isPending}
              >
                {saveMutation.isPending ? 'Guardando...' : isEditing ? 'Guardar' : 'Crear'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
}
